import i18n from "@/locales"
import { DevLog } from "@/helpers/consoleHelpers"
import Dialog from 'quasar/src/plugins/Dialog.js';
import { onMounted, ref } from "vue"
import type { DialogChainObject } from 'quasar'

import commonMessageKeys from '@/locales/messages/common-message-keys'

const refreshing = ref<boolean>(false)
const updateExists = ref<boolean>(false)
const registration = ref<ServiceWorkerRegistration|null>(null)
const updateDialog = ref<DialogChainObject|null>(null)

export function useAppUpdater(declareEvents = true) {
    onMounted(() => {
        if (declareEvents && 'serviceWorker' in navigator) {
            document.addEventListener('swUpdated', updateAvailable, { once: true })
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (refreshing.value) {
                    return
                } 
    
                refreshing.value = true
                window.location.reload()
            })
        }
    })

    const updateAvailable = (event: Event|null) => {
        registration.value = (event as CustomEvent)?.detail
        updateExists.value = true
        if (updateDialog.value) {
            updateDialog.value.hide()
            updateDialog.value = null
        }

        updateDialog.value = Dialog.create({
            class: 'my-app-updater',
            message: i18n.global.t(commonMessageKeys.updateAvailable),
            persistent: true,
            cancel: {
                color: 'disabled',
                textColor: 'dark',
                rounded: true,
                unelevated: true,
                noCaps: true,
                label: i18n.global.t(commonMessageKeys.updateCancel),
            }, 
            ok: {
                textColor: 'dark',
                rounded: true,
                unelevated: true,
                noCaps: true,
                label: i18n.global.t(commonMessageKeys.updateInstall)
            } 
        }).onOk(() => {
            DevLog.log("Force update requested by user")
            refreshApp()
        }).onCancel(() => {
            DevLog.log("Cancel update requested by user")
        }).onDismiss(() => {
            DevLog.log("Dismiss update requested by user")
        })
    }

    const waitingForUpdate = () => {
        return updateExists.value
    }

    const refreshApp = () => {
        updateExists.value = false
        if (!registration.value || !registration.value.waiting) {
            // Make sure we only send a 'skip waiting' message if the SW is waiting
            return
        }

        // Send message to SW to skip the waiting and activate the new SW
        registration.value.waiting.postMessage({ type: 'SKIP_WAITING' })
    }

    return {
        refreshApp,
        waitingForUpdate
    }
}